import { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  Input,
  NumberInput,
  RegularModal,
  Select,
  UserSelect,
} from 'components'
import { DetectBlur } from 'containers/util-containers'
import { Field, Form, IssueCategoryForSettings } from '..'
import { Color, SettingsColorPicker } from '../common'
import { IssueSeverity } from 'modules/issues'
import { useIssueSeverityOptions } from 'modules/issues/consts'
import styled from 'styled-components'

const Modal = styled(RegularModal)`
  .content {
    display: flex;

    & > div:first-child {
      width: 50%;
      padding-right: 1rem;
    }
    & > div:last-child {
      width: 50%;
      padding-left: 1rem;
      border-left: 1px solid ${props => props.theme.colors.gray9};
    }
  }
`

const ExplanationLabel = styled.label`
  margin: 6px 0 6px 6px !important;
`

const CheckBoxField = styled(Field)`
  & > div {
    justify-content: left;
  }
`

export interface IssueCategoryForm {
  name: string
  color: string
  defaultSeverity: IssueSeverity
  responsibleUser?: string | null
  automaticReminderInterval: number
  minimumSeverityForReminder: IssueSeverity | null
  remindResponsible: boolean
  remindCreator: boolean
}

interface IssueCategoryPromptProps {
  category?: IssueCategoryForSettings | null
  initialData?: IssueCategoryForm
  editableCategories: IssueCategoryForSettings[]
  uneditableCategories: IssueCategoryForSettings[]

  onSubmit: PromptResolve<IssueCategoryForm | null>
}

export const IssueCategoryPrompt: React.FC<IssueCategoryPromptProps> = ({
  category,
  initialData,
  editableCategories,
  uneditableCategories,
  onSubmit,
}) => {
  const translations = useTranslate({
    form: {
      label: {
        name: 'common.name',
        color: 'common.color',
        responsibleUser: 'settings.responsible-user',
        automaticReminderInterval: 'settings.automatic-reminder-interval',
        automaticReminderIntervalInfo:
          'settings.automatic-reminder-interval-explaination',
        minimumSeverityForReminder: 'settings.minimum-severity-for-reminder',
        remindResponsibleUser: 'settings.remind-responsible-user',
        remindCreator: 'settings.remind-creator',
      },
    },

    addIssueCategory: 'settings.add-issue-category',
    editIssueCategory: 'settings.edit-issue-category',
    issueCategories: 'common.issue-categories',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      nameExists: 'settings.validation.issue-category-name-exists',
      required: 'common.required',
    },
    prompt: {
      delete: ['settings.prompts.issue-category-delete', { name: '' }],
      deleteTitle: 'settings.prompts.issue-category-delete-title',
    },
    defaultSeverity: 'common.default-severity',
  })

  const severityOptions = useIssueSeverityOptions()

  const editing = typeof initialData !== 'undefined'
  const editable =
    editableCategories.map(cat => cat.id).includes(category?.id ?? '') ||
    !category

  function validateName(name: string, formName: string) {
    if (name === initialData?.name) return null
    if (name === '') return translations.validation.required
    if (
      !![...editableCategories, ...uneditableCategories].find(
        type => type.name === formName
      )
    ) {
      return translations.validation.nameExists
    }
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formChangeHandler: handler,
    updateForm,
    submitHandler: submit,
  } = useForm<IssueCategoryForm>({
    values: initialData ?? {
      name: '',
      color: 'hsl(138deg, 80%, 80%)',
      defaultSeverity: 'MEDIUM',
      responsibleUser: null,
      automaticReminderInterval: 0,
      minimumSeverityForReminder: 'HIGH',
      remindResponsible: false,
      remindCreator: false,
    },
    validators: {
      name: (val, form) => validateName(val, form.name),
    },
  })

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <Modal
        title={
          editing
            ? translations.editIssueCategory
            : translations.addIssueCategory
        }
        submitText={editing ? translations.update : translations.create}
        submitDisabled={!formValid}
        width="800px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <div>
          <Form>
            {editable && (
              <Field>
                <label>{translations.form.label.name}</label>
                <Input
                  value={form.name}
                  error={errors.name}
                  autoFocus
                  fullWidth
                  onChange={handler('name')}
                  grayed
                />
              </Field>
            )}

            <Field>
              <label>{translations.form.label.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser ?? null}
                fullWidth
                onChange={responsibleUser =>
                  updateForm({ responsibleUser: responsibleUser })
                }
                grayed
              />
            </Field>
            <Field>
              <label>{translations.defaultSeverity}</label>
              <Select
                value={form.defaultSeverity}
                options={severityOptions}
                nullable={false}
                onChange={value =>
                  value ? updateForm({ defaultSeverity: value }) : void 0
                }
                fullWidth
                grayed
              />
            </Field>
            {editable && (
              <Field>
                <label>{translations.form.label.color}</label>
                <Color color={form.color}>
                  <SettingsColorPicker
                    value={form.color}
                    onChange={handler('color')}
                  />
                </Color>
              </Field>
            )}
          </Form>
        </div>
        <div>
          <Form>
            <Field>
              <label>{translations.form.label.automaticReminderInterval}</label>
              <NumberInput
                value={form.automaticReminderInterval}
                onChange={handler('automaticReminderInterval')}
                min={0}
                grayed
              />
              <ExplanationLabel>
                {translations.form.label.automaticReminderIntervalInfo}
              </ExplanationLabel>
            </Field>
            {form.automaticReminderInterval !== null &&
              form.automaticReminderInterval !== 0 &&
              form.automaticReminderInterval !== undefined && (
                <>
                  <Field>
                    <label>
                      {translations.form.label.minimumSeverityForReminder}
                      <Select
                        value={form.minimumSeverityForReminder}
                        options={severityOptions}
                        nullable={false}
                        onChange={value =>
                          value
                            ? updateForm({ minimumSeverityForReminder: value })
                            : void 0
                        }
                        fullWidth
                        grayed
                      />
                    </label>
                  </Field>
                  <CheckBoxField>
                    <Checkbox
                      checked={form.remindResponsible}
                      onChange={handler('remindResponsible')}
                      label={translations.form.label.remindResponsibleUser}
                      grayed
                    />
                  </CheckBoxField>
                  <CheckBoxField>
                    <Checkbox
                      checked={form.remindCreator}
                      onChange={handler('remindCreator')}
                      label={translations.form.label.remindCreator}
                      grayed
                    />
                  </CheckBoxField>
                </>
              )}
          </Form>
        </div>
      </Modal>
    </DetectBlur>
  )
}
